// src/common/PaginatedTable.jsx
import { useMemo } from "react";
import DataTable from "./DataTable";
import Pagination from "./Pagination";

export default function PaginatedTable({
  // table props
  columns = [],
  data = [],
  emptyMessage,
  rowKey,
  rowClassName,
  rowStyle,
  cellClassName,
  wrapperClassName,

  // pagination props
  mode = "client",
  page = 1,
  pageSize = 10,
  total, // optional (if omitted, uses data.length)
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,

  // ✅ optional footer row (ex: MTD Total)
  footerRow = null,
  reserveFooterRow = false, // if true, pageSize includes footerRow
}) {
  /*
   * Pagination contract:
   * - mode="client": pass the full dataset in `data`; this component slices rows for the current page.
   * - mode="server": pass only the current page of rows in `data`; this component must NOT slice again.
   *
   * For server mode, the parent component is responsible for:
   * - fetching rows with page/pageSize-derived params (for example limit/offset)
   * - passing the backend total count via `total`
   * - resetting page to 1 when filters or page size change
   *
   * Do not add auto-detection heuristics here. Call sites must choose the mode explicitly
   * whenever server-paginated data is involved.
   */
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const safePageSize = Math.max(1, Number(pageSize) || 10);
  const paginationMode = mode === "server" ? "server" : "client";

  const computedTotal = useMemo(() => {
    const t = Number(total);
    if (Number.isFinite(t) && t >= 0) return t;
    return safeData.length;
  }, [total, safeData.length]);

  const rowsPerPage = useMemo(() => {
    if (reserveFooterRow && footerRow) return Math.max(0, safePageSize - 1);
    return safePageSize;
  }, [reserveFooterRow, footerRow, safePageSize]);

  const start = useMemo(
    () => (Math.max(1, Number(page) || 1) - 1) * rowsPerPage,
    [page, rowsPerPage],
  );

  // ✅ Server mode: pass only the current page of rows in `data`; this component must NOT slice.
  // ✅ Client mode: pass the full dataset in `data`; this component slices rows for the current page.
  const displayedData = useMemo(() => {
    const baseRows =
      paginationMode === "server" ? safeData : safeData.slice(start, start + rowsPerPage);
    if (footerRow) return [...baseRows, footerRow];
    return baseRows;
  }, [paginationMode, safeData, start, rowsPerPage, footerRow]);

  // When page size changes, reset back to page 1
  const handlePageSizeChange = (n) => {
    onPageChange?.(1);
    onPageSizeChange?.(n);
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={displayedData}
        emptyMessage={emptyMessage}
        rowKey={rowKey}
        rowClassName={rowClassName}
        rowStyle={rowStyle}
        cellClassName={cellClassName}
        wrapperClassName={wrapperClassName}
      />

      <div className="px-4 pb-4 pt-4">
        <Pagination
          page={page}
          pageSize={safePageSize}
          itemPageSize={rowsPerPage}
          total={computedTotal}
          onPageChange={onPageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={pageSizeOptions}
        />
      </div>
    </>
  );
}
